import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, FormControlLabel, Checkbox, Grid } from '@mui/material';
import { TextFieldComponent } from '../../../../components/common';
import { type ProcedenciaFormData, type ProcedenciaFormErrors, type ProcedenciaRequest, type Procedencia } from '../types/procedencia';

interface EditProcedenciaProps {
  open: boolean;
  procedencia: Procedencia | null;
  onClose: () => void;
  onSave: (procedencia: ProcedenciaRequest) => Promise<void>;
  title: string;
}

const initialFormState: ProcedenciaFormData = {
  descripcion: '',
  observacion: '',
  activo: true,
};

export const EditProcedencia: React.FC<EditProcedenciaProps> = ({
  open,
  procedencia,
  onClose,
  onSave,
  title,
}) => {
  const [formData, setFormData] = useState<ProcedenciaFormData>(initialFormState);
  const [errors, setErrors] = useState<ProcedenciaFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      if (procedencia) {
        setFormData({
          descripcion: procedencia.descripcion,
          observacion: procedencia.observacion || '',
          activo: procedencia.activo,
        });
      } else {
        setFormData(initialFormState);
      }
      setErrors({});
    }
  }, [open, procedencia]);

  const validateForm = (): boolean => {
    const newErrors: ProcedenciaFormErrors = {};

    if (!formData.descripcion.trim()) {
      newErrors.descripcion = 'La descripción es requerida';
    } else if (formData.descripcion.trim().length < 2) {
      newErrors.descripcion = 'La descripción debe tener al menos 2 caracteres';
    } else if (formData.descripcion.trim().length > 100) {
      newErrors.descripcion = 'La descripción no puede exceder 100 caracteres';
    }

    if (formData.observacion && formData.observacion.trim().length > 255) {
      newErrors.observacion = 'La observación no puede exceder 255 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof ProcedenciaFormData) => (value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof ProcedenciaFormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, activo: event.target.checked }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const request: ProcedenciaRequest = {
        idProcedencia: procedencia?.idProcedencia,
        descripcion: formData.descripcion.trim(),
        observacion: formData.observacion?.trim() || undefined,
        activo: formData.activo,
      };

      await onSave(request);
    } catch (error) {
      console.error('Error saving procedencia:', error);
      setErrors({ descripcion: 'Error al guardar la procedencia' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextFieldComponent
                label="Descripción"
                value={formData.descripcion}
                onChange={handleInputChange('descripcion')}
                error={errors.descripcion}
                required
                fullWidth
                disabled={isSubmitting}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextFieldComponent
                label="Observación"
                value={formData.observacion}
                onChange={handleInputChange('observacion')}
                error={errors.observacion}
                fullWidth
                multiline
                rows={3}
                disabled={isSubmitting}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.activo}
                    onChange={handleCheckboxChange}
                    disabled={isSubmitting}
                  />
                }
                label="Activo"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSubmitting}>
          {isSubmitting ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
